import { column, Css, GridColumn, GridDataRow, GridTable, numericColumn, simpleHeader } from "@homebound/beam";
import { priceCell } from "src/components/gridTableCells";
import {
  PlanBasedLineItemsTab_LineItemFragment,
  PlanBasedLineItemsTab_PlanBasedLineItemFragment,
} from "src/routes/projects/commitments/components/PlanBasedLineItemsTab.generated";

type PlanBasedBidLinesSectionProps = {
  planBasedLineItems: PlanBasedLineItemsTab_PlanBasedLineItemFragment[];
  lineItems: PlanBasedLineItemsTab_LineItemFragment[];
};

export function PlanBasedBidLinesSection(props: PlanBasedBidLinesSectionProps) {
  const { planBasedLineItems, lineItems } = props;
  return (
    <section data-testid="planBasedBidLinesSection">
      <h2 css={Css.mdSb.mt3.mb1.$}>Plan Based Bid Lines</h2>
      <GridTable columns={columns} rows={createRows(planBasedLineItems, lineItems)} />
    </section>
  );
}

type HeaderRow = { kind: "header" };
type DataRow = {
  kind: "data";
  data: {
    option: string;
    dimensions: string;
    committedInCents: number;
    billedInCents: number;
    unbilledInCents: number;
    paidInCents: number;
    pendingInCents: number;
  };
};
type Row = HeaderRow | DataRow;

const columns: GridColumn<Row>[] = [
  column<Row>({ header: () => "Option", data: ({ option }) => option }),
  column<Row>({ header: () => "Dimensions", data: ({ dimensions }) => dimensions }),
  numericColumn<Row>({
    header: () => "Committed",
    data: ({ committedInCents }) => priceCell({ id: "committed", valueInCents: committedInCents }),
  }),
  numericColumn<Row>({
    header: () => "Billed",
    data: ({ billedInCents }) => priceCell({ id: "billed", valueInCents: billedInCents }),
  }),
  numericColumn<Row>({
    header: () => "Unbilled",
    data: ({ unbilledInCents }) => priceCell({ id: "unbilled", valueInCents: unbilledInCents }),
  }),
  numericColumn<Row>({
    header: () => "Paid to Date",
    data: ({ paidInCents }) => priceCell({ id: "paid", valueInCents: paidInCents }),
  }),
  numericColumn<Row>({
    header: () => "Pending",
    data: ({ pendingInCents }) => priceCell({ id: "pending", valueInCents: pendingInCents }),
  }),
];

function createRows(
  planBasedLineItems: PlanBasedLineItemsTab_PlanBasedLineItemFragment[],
  lineItems: PlanBasedLineItemsTab_LineItemFragment[],
): GridDataRow<Row>[] {
  const dataRows = planBasedLineItems.map((pbl) => {
    const matching = lineItems.filter((cli) => cli.primaryBidContractLineItem?.id === pbl.id);
    return {
      kind: "data" as const,
      id: pbl.id,
      data: {
        option: pbl.groupLine.group.displayName,
        dimensions: pbl.groupLine.options.map((o) => o.name).join(" "),
        committedInCents: matching.sum((cli) => cli.committedInCents),
        billedInCents: matching.sum((cli) => cli.billedInCents ?? 0),
        unbilledInCents: matching.sum((cli) => cli.unbilledInCents),
        paidInCents: matching.sum((cli) => cli.paidInCents ?? 0),
        pendingInCents: matching.sum((cli) => cli.pendingBilledInCents),
      },
    };
  });
  return [simpleHeader, ...dataRows];
}
